

















































































import { Component, Emit, InjectReactive, PropSync, Vue, Watch } from 'vue-property-decorator';
import { Correction, FeedApi, FeedCorrection, FeedTank, FeedTypeLevelMetricEnum } from '@/api';
import { ParsingUtil } from '@/common/ParsingUtil';
import debounce from 'lodash.debounce';
import _ from 'lodash';
import { CorrectionService } from '@/services/correction-service';
import { CustomFormulaResult, Millimeters } from '@/typings/custom-formula-calculator';
import { FeedTankService } from '@/pages/account/products/feed/services';
import Toast from '@/components/display/toast.vue';

@Component({
	methods: { debounce },
	components: {},
})
export default class FeedTankCorrectionSettings extends Vue {
	public readonly smallScreen = window.innerWidth;
	public calculationError: string | null = null;

	@InjectReactive('selectedFeedTank')
	public feedTank!: FeedTank;

	@PropSync('savedCorrections')
	public _savedCorrections!: FeedCorrection[];

	@PropSync('parameters')
	public _parameters!: Record<string, string | number>;

	@PropSync('correction')
	public _correction!: Record<string, number | null>;

	public fields: any[] = [];

	public calculatedTank: CustomFormulaResult | Error | null = null;

	public tankVolumes: {
		label: string;
		object: any;
		field: string;
		modify: (value: number) => number;
	}[] = this.buildTankVolumes();

	private feedTankService: FeedTankService | null = null;
	private toast: Toast = new Toast();

	public unitLabel() {
		return this.isTonnes() ? 'Weight' : 'Volume';
	}

	public isTonnes() {
		return this.feedTank.feedtype?.levelMetric == FeedTypeLevelMetricEnum.Weight;
	}

	public convertToTonnes(value: number) {
		return this.isTonnes()
			? this.$options.filters?.tonnes(Math.round(value * (this.feedTank.feedtype?.density ?? 1)))
			: this.$options.filters?.numeric(value) + ' L';
	}

	public calculateTotal(field: string): number {
		return (
			ParsingUtil.parseInt(this._correction[field]) + ParsingUtil.parseInt(this._parameters[field])
		);
	}

	public buildTankVolumes(): {
		label: string;
		object: any;
		field: string;
		modify: (value: number) => number;
	}[] {
		return [
			{
				label: 'Nominal',
				object: this.feedTank,
				field: 'nominalVolume',
				modify: this.convertToTonnes,
			},
			{
				label: 'Max',
				object: this.calculatedTank,
				field: 'maxVolume',
				modify: this.convertToTonnes,
			},
		];
	}

	@Watch('feedTank')
	public async created() {
		this.feedTankService = new FeedTankService(this.feedTank);
		this.fields =
			(<(object & { inputs: any }) | null>this.feedTank?.formula)?.inputs
				?.replace(/(\r\n|\n|\r)/g, '\\n')
				?.split('\\n')
				?.map((input: any) => {
					const [type, name, label, parameters] = input.split('|');
					return { type, name, label, parameters: parameters?.split(',') };
				}) ?? [];
		await this.updateTank();
	}

	public async updateCorrections(e: Event): Promise<void> {
		e.preventDefault();
		if (!this.feedTank?.id || !this.feedTankService) return;
		const { errorMessage, data } = await this.feedTankService.updateFeedTankFormulaCorrection(
			this.feedTank,
			this._correction,
		);

		if (errorMessage) {
			console.error(errorMessage);
			return;
		}

		this.toast.makeToast('Updated formula corrections', 'Success', 'success');
	}

	public setCorrectionListDebounced = _.debounce(this.setCorrectionList, 20);
	public async setCorrectionList(): Promise<void> {
		this.onUpdateCorrection();
	}

	public async updateTank(): Promise<void> {
		const calculation = await CorrectionService.calculateFeedTankVolume(
			[<Millimeters>9999],
			{},
			this.feedTank ?? null,
			this._parameters,
			this._correction,
		);
		this.calculationError = calculation?.error ?? null;
		if (this.calculationError) console.error(this.calculationError);
		this.calculatedTank = calculation?.result ?? null;
		this.tankVolumes = this.buildTankVolumes();
	}

	@Emit('updateCorrection')
	public onUpdateCorrection(): FeedTankCorrectionSettings {
		return this;
	}

	public formatFormulaError() {
		return (
			this.calculationError?.substring(0, this.calculationError?.indexOf('Formula =') ?? 0) ??
			'Unknown error'
		).trim();
	}
}
